import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Box, Heading, Text } from 'localmed-core';
import React, { useState } from 'react';
import { withRouter } from 'react-router';
import { Tabs } from 'react-tabs';
import { Field } from '../../../../../legacyCore/Form';
import Gallery from '../../../../../shared/components/Gallery/index';
import InsuranceIssuerSelectWithData from '../../../../../shared/components/InsuranceIssuerSelect';
import {
  ProfilePanelList,
  ProfileTab,
  ProfileTabList,
  ProfileTabPanel,
} from '../../../../../shared/components/ProfileTabs';
import { CASH_PAYMENT_ID } from '../../../../../shared/constants';
import { getQueryFromLocation, updateParams } from '../../../../../shared/utils/urls';
import Truncate from '../../../../../widgets/components/Truncate';
import OfficeSelect from './OfficeSelect';

const Section = styled(Box)(
  ({ theme }) => css`
    border-top: 1px solid ${theme.colors.legacyBorder};
    padding: ${theme.space.sm};

    &:first-of-type {
      border-top: 0;
    }
  `
);

const Small = styled('span')(
  ({ theme }) => css`
    font-weight: ${theme.fontWeights.normal};
    color: ${theme.colors.gray};
  `
);

const List = styled('ul')`
  padding: 0;
  list-style: none;
`;
const ListItem = styled('li')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    margin-bottom: ${theme.space.xs};
  `
);

const Logo = styled('img')(
  ({ theme }) => css`
    padding-right: ${theme.space.sm};
  `
);

const StyledHeading = styled(Heading)(
  ({ theme }) => css`
    font-weight: ${theme.fontWeights.bold};
    margin-bottom: ${theme.space.sm};
  `
);

function plurialize(length, value) {
  if (length > 1) return `${value}s`;
  return value;
}

function notEmpty(value) {
  return value.length !== 0;
}

function ProviderPageTab({
  provider,
  activeOfficeId,
  setActiveOfficeId,
  history,
  ...containerProps
}) {
  const [values, setValues] = useState({});
  const {
    display,
    office,
    allProvidersForProfile,
    specialties: { nodes: specialtiesNodes },
    profile: { bio, degrees, affiliations, languageNames, accreditationNames, agesTreated },
  } = provider;
  const offices = allProvidersForProfile.nodes.map((value) => value.office);
  const query = getQueryFromLocation(history.location);
  const numOffices = offices.length;

  return (
    <Box {...containerProps}>
      <Tabs>
        <ProfileTabList>
          <ProfileTab>Overview</ProfileTab>
          <ProfileTab>Background</ProfileTab>
        </ProfileTabList>
        <ProfilePanelList>
          <ProfileTabPanel>
            <Section>
              <StyledHeading tier="h4">
                {plurialize(numOffices, 'Office')}{' '}
                {numOffices > 1 && (
                  <Small>
                    {display} has {numOffices} {plurialize(numOffices, 'location')}
                  </Small>
                )}
              </StyledHeading>
              <OfficeSelect
                offices={offices}
                activeOfficeId={activeOfficeId}
                setActiveOfficeId={setActiveOfficeId}
              />
              {office.photos.nodes && <Gallery images={office.photos.nodes} pt="sm" />}
            </Section>
            {specialtiesNodes && (
              <Section>
                <StyledHeading tier="h4">Specialties</StyledHeading>
                <List>
                  {specialtiesNodes.map((specialty) => (
                    <li key={specialty.id}>{specialty.specialistName}</li>
                  ))}
                </List>
              </Section>
            )}

            {office.insuranceCoverageEnabled && (
              <Section>
                <StyledHeading tier="h4">Insurance Checker</StyledHeading>
                {provider.office.insuranceCoverageEnabled && (
                  <Field name="insuranceIssuer" htmlFor="insuranceIssuer-input" label="Payment">
                    <InsuranceIssuerSelectWithData
                      id="insuranceIssuer"
                      gray
                      providerId={provider.id}
                      selectedItem={values.insuranceIssuer}
                      initialSelectedValue={query.insurance_issuer_id || CASH_PAYMENT_ID}
                      onChange={(value) => {
                        setValues({ ...values, insuranceIssuer: value });
                        updateParams(
                          history,
                          { insurance_issuer_id: value && value.id },
                          { replace: true }
                        );
                      }}
                    />
                  </Field>
                )}
              </Section>
            )}
            {bio && (
              <Section>
                <StyledHeading tier="h4">Bio</StyledHeading>
                <Truncate text={bio} length={200} />
              </Section>
            )}
          </ProfileTabPanel>
          <ProfileTabPanel>
            {notEmpty(degrees) && (
              <Section>
                <StyledHeading tier="h4">Education &amp; Training</StyledHeading>
                <List>
                  {degrees.map((degree) => (
                    <li key={degree.degreeType}>
                      <Text color="gray" fontSize="sm">
                        {degree.degreeType}
                      </Text>
                      <Text>{degree.university}</Text>
                    </li>
                  ))}
                </List>
              </Section>
            )}
            {notEmpty(affiliations) && (
              <Section>
                <StyledHeading tier="h4">Memberships</StyledHeading>
                <List>
                  {affiliations.map((affiliation) => (
                    <ListItem key={affiliation.id}>
                      {affiliation.affiliate.logo && (
                        <Logo
                          src={affiliation.affiliate.logo}
                          alt={`${affiliation.display} Logo`}
                          width="32"
                          height="32"
                        />
                      )}
                      {affiliation.display}
                    </ListItem>
                  ))}
                </List>
              </Section>
            )}
            {notEmpty(languageNames) && (
              <Section>
                <StyledHeading tier="h4">Languages Spoken</StyledHeading>
                <ul>
                  {languageNames.map((language) => (
                    <li key={language.name}>{language.name}</li>
                  ))}
                </ul>
              </Section>
            )}
            {notEmpty(accreditationNames) && (
              <Section>
                <StyledHeading tier="h4">Accreditations</StyledHeading>
                <ul>
                  {accreditationNames.map((accreditation) => (
                    <li key={accreditation.name}>{accreditation.name}</li>
                  ))}
                </ul>
              </Section>
            )}
            <Section>
              <StyledHeading tier="h4">Ages Treated</StyledHeading>
              <Text>{agesTreated.display}</Text>
            </Section>
          </ProfileTabPanel>
        </ProfilePanelList>
      </Tabs>
    </Box>
  );
}

export default withRouter(ProviderPageTab);
